import {
    EPunchSetApprovalType, ITimecardPunchSet, IExpenseDocument, IPayrollStatusDocument, IDate, IUserDocument,
    EDayOfWeek, ITimecard, ITimecardDates, IPayGroup, IDateRange, IObjectTimecardDocument, ISuspensePunchData,
    IPunchSetDocument, ELoggedInUserType, IPayrollPayCalendar, IPaySettings, IPayrollPayGroupData, ITimecardWeek, ITimecardDay
} from "../models"
import * as payGroupAndTypeHelper from "./payGroupAndTypeHelper"
import moment, { Moment } from "moment"
import _ from "lodash"
import * as timeHelper from "./timeHelper"
import strings from "../localization/strings"
import * as permissionHelper from "./permissionHelper"
import AuthenticationStore from "../stores/AuthenticationStore"
import * as payrollHelper from "./payrollHelper"
import * as timeTotalsHelper from "./timeTotalsHelper"
import * as utils from "./utils"
import * as payrollStatusHelper from "./payrollStatusHelper"

export function getTimecardStartDay(user: IUserDocument): EDayOfWeek {
    const payGroup = payGroupAndTypeHelper.getPayGroupByPayrollPayGroupData({
        PayrollHierarchyItemId: user.PayrollHierarchyItemId,
        PayrollPayGroupId: user.PayrollPayGroupId
    })
    return payGroup ? payGroup.PaySettings.PayPeriodStartDay : EDayOfWeek.Monday
}

export function getTimecardPositionLabel(timecard: ITimecard): string {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    })
    let positionLabel
    if (timecard.Position) {
        positionLabel = timecard.Position.Name
        let payInfo
        if (timecard.PayClassId) {
            const payClass = timecard.Position.AvailablePayClasses.find(i => i.Id === timecard.PayClassId)
            if (payClass) {
                positionLabel += `-${payClass.Name}`
                if (payClass.PayInfo)
                    payInfo = payClass.PayInfo
            }
        }
        else {
            payInfo = timecard.Position.PayInfo
        }

        if (permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) && payInfo) {
            positionLabel += " — Bill Rate: " + formatter.format(payInfo.BillRate)
            positionLabel += ", Pay Rate: " + formatter.format(payInfo.PayRate)
        }
    }
    return positionLabel
}

export function getTimecardDateByPayrollPayGroupData(referenceDate: IDate, payrollPayGroupData: IPayrollPayGroupData): ITimecardDates {
    const payGroup = payGroupAndTypeHelper.getPayGroupByPayrollPayGroupData(payrollPayGroupData)
    return getTimecardDateByPayGroup(referenceDate, payGroup)
}

export function getTimecardDateByPayGroupId(referenceDate: IDate, payGroupId: string): ITimecardDates {
    const payGroup = payGroupAndTypeHelper.getPayGroupById(payGroupId)
    return getTimecardDateByPayGroup(referenceDate, payGroup)
}

export function getTimecardDateRangeList(referenceDates: IDate[], paySettings: IPaySettings): IDateRange[] {
    const timecardDateRangeList: IDateRange[] = []
    referenceDates.forEach(referenceDateUTCLocal => {
        const referenceDate = timeHelper.keepConvertUTCToLocal(referenceDateUTCLocal)
        if (!timecardDateRangeList.find(i => moment(i.StartDate).isSameOrBefore(referenceDate) && moment(referenceDate).isSameOrBefore(i.EndDate))) {
            const payCalendar = payrollHelper.getPayrollPayCalendarByPayGroupPaySettings(paySettings)
            const timecardDate = getTimecardDateByPayrollPayCalendar(referenceDate, payCalendar)
            timecardDateRangeList.push({
                StartDate: timecardDate.StartDate,
                EndDate: timecardDate.EndDate
            })
        }
    })
    return timecardDateRangeList
}

export function getTimecardDateByPayGroup(referenceDate: IDate, payGroup: IPayGroup): ITimecardDates {
    return getTimecardDateByPayrollPayCalendar(referenceDate, payrollHelper.getPayrollPayCalendarByPayGroup(payGroup))
}

export function getTimecardDateByPayrollPayCalendar(referenceDate: IDate, payrollPayCalendar: IPayrollPayCalendar): ITimecardDates {
    const startDate = startOfWeek(moment(referenceDate).add(-1 * payrollPayCalendar.PayPeriodDayStartTime24Hour, "hour"), payrollPayCalendar.PayPeriodStartDay).startOf("day")
    const endDate = _.cloneDeep(startDate).add(payrollPayCalendar.PayPeriodNumberOfWeeks * 7, "day").add(-1, "day").endOf("day")
    const startDateTime = startDate.add(payrollPayCalendar.PayPeriodDayStartTime24Hour, "hour").format()
    const endDateTime = endDate.add(payrollPayCalendar.PayPeriodDayStartTime24Hour, "hour").format()
    return {
        StartDate: startDateTime,
        EndDate: endDateTime,
        StartDay: payrollPayCalendar.PayPeriodStartDay
    }
}

function startOfWeek(referenceDate: IDate, startOfWeek: EDayOfWeek): Moment {
    let diff = moment(referenceDate).day() - startOfWeek
    if (diff < 0)
        diff += 7
    return moment(referenceDate).add(-1 * diff, "day")
}

export function timecardPartialLocked(userId: string, timecardStartDate: IDate, timecardEndDate: IDate, lockData: IPayrollStatusDocument[], approvalType: EPunchSetApprovalType): boolean {
    return lockData.find(i =>
        payrollStatusHelper.isLocked([i], userId, approvalType === EPunchSetApprovalType.Employee ? ELoggedInUserType.Employee : ELoggedInUserType.AccountUser) &&
        (
            (moment(timecardStartDate).isSameOrAfter(i.StartDate) && moment(timecardStartDate).isSameOrBefore(i.EndDate)) ||
            (moment(timecardEndDate).isSameOrAfter(i.StartDate) && moment(timecardEndDate).isSameOrBefore(i.EndDate))
        )
    ) ? true : false
}

export function timecardDayLocked(userId: string, timecardDayLocalStartDayTime: string, lockData: IPayrollStatusDocument[], priorPeriodAdjustMode: boolean, allowPriorPeriodAdjust: boolean) {
    if (priorPeriodAdjustMode) {
        return allowPriorPeriodAdjust ?
            false :
            true
    }
    else {
        const startDayTime = timecardDayLocalStartDayTime.endsWith("Z") ? timecardDayLocalStartDayTime : timecardDayLocalStartDayTime + "Z"
        const locked = lockData.length === 0 ?
            false :
            payrollStatusHelper.isLocked([moment(startDayTime).utc().isBefore(moment(lockData[0].EndDate)) ? lockData[0] : lockData[1]], userId, ELoggedInUserType.AccountUser)
        return locked
    }
}

export function timecardHasExpenses(timecard: IObjectTimecardDocument): boolean {
    let returnVal = false
    timecard.MasterTimecard.TimecardWeeks.forEach(week => {
        week.Days.forEach(day => {
            if (day.Expenses && day.Expenses.length > 0) {
                returnVal = true
            }

            if (day.TimeExpenses && day.TimeExpenses.length > 0) {
                returnVal = true
            }
        })
    })

    return returnVal
}

export function getApproveState(punchSets: (ITimecardPunchSet | IPunchSetDocument)[], expenses: IExpenseDocument[], approvalType: EPunchSetApprovalType): boolean {
    const approvePropertyName = approvalType === EPunchSetApprovalType.ExtSupervisor ?
        "ExtSupApproved" :
        approvalType === EPunchSetApprovalType.Supervisor ?
            "SupervisorApproved" :
            "EmployeeApproved"
    const punchSetsApproved = punchSets.find(ps => !ps.Calculated && !ps[approvePropertyName]) ? false : true
    const expensesApproved = approvalType === EPunchSetApprovalType.ExtSupervisor ?
        true :
        expenses.find(expense => !expense[approvePropertyName]) ? false : true
    return punchSetsApproved && expensesApproved
}

export function getTimecardAllExpenses(timecard: ITimecard): IExpenseDocument[] {
    let allExpenses: IExpenseDocument[] = []
    timecard.TimecardWeeks.forEach(tw =>
        tw.Days.forEach(twd => {
            if (twd.Expenses) {
                allExpenses = allExpenses.concat(twd.Expenses)
            }
            if (twd.TimeExpenses) {
                allExpenses = allExpenses.concat(twd.TimeExpenses)
            }

        })
    )
    return allExpenses
}

export function getSuspenseReasonsByDateRange(dateRange: IDateRange, suspensePunchData: ISuspensePunchData): string[] {
    let suspenseReasons: string[] = []
    if (suspensePunchData) {
        const reviewSuspensePunchData = suspensePunchData.ReviewSuspensePunchData.filter(i => moment(dateRange.StartDate).isSameOrBefore(i.DateTimeLocal) && moment(dateRange.EndDate).isSameOrAfter(i.DateTimeLocal))
        const updateTimePunchData = suspensePunchData.ReviewSuspenseUpdateTimeData.filter(i => moment(dateRange.StartDate).isSameOrBefore(i.DateTimeLocal) && moment(dateRange.EndDate).isSameOrAfter(i.DateTimeLocal))
        const allUpdateTimePunchIds = suspensePunchData.ReviewSuspenseUpdateTimeData.map(i => i.Id)
        reviewSuspensePunchData.forEach(i => {
            suspenseReasons.push(allUpdateTimePunchIds.includes(i.Id) ?
                strings.Edit_punch_time_from_mobile :
                strings.Add_missing_punch_from_mobile
            )
        })
        updateTimePunchData.forEach(i => {
            !reviewSuspensePunchData.find(e => e.Id === i.Id) &&
                suspenseReasons.push(strings.Edit_punch_time_from_mobile)
        })
    }
    return suspenseReasons
}

export function getSuspenseReasonsFromPunchSets(punchSets: IPunchSetDocument[], suspensePunchData: ISuspensePunchData): string[] {
    const punchIds = getAllPunchIdsFromPunchSets(punchSets)
    return getSuspenseReasonsFromPunches(punchIds, suspensePunchData)
}

export function getSuspenseReasonsFromPunches(punchIds: string[], suspensePunchData: ISuspensePunchData): string[] {
    let suspenseReasons: string[] = []
    if (suspensePunchData) {
        const editTimePunchIds = suspensePunchData.ReviewSuspenseUpdateTimeData.map(i => i.Id)
        const addMissingPunchIds = suspensePunchData.ReviewSuspensePunchData.map(i => i.Id).filter(i => !editTimePunchIds.includes(i))
        punchIds.forEach(punchId => {
            addMissingPunchIds.includes(punchId) &&
                suspenseReasons.push(strings.Add_missing_punch_from_mobile)
            editTimePunchIds.includes(punchId) &&
                suspenseReasons.push(strings.Edit_punch_time_from_mobile)
        })
    }
    return suspenseReasons
}

export function getAllPunchIdsFromPunchSets(punchSets: IPunchSetDocument[]): string[] {
    let allPunchIds: string[] = []
    punchSets &&
        punchSets.forEach(punchSet => {
            punchSet.PunchInId && !allPunchIds.includes(punchSet.PunchInId) &&
                allPunchIds.push(punchSet.PunchInId)
            punchSet.PunchOutId && !allPunchIds.includes(punchSet.PunchOutId) &&
                allPunchIds.push(punchSet.PunchOutId)
        })
    return allPunchIds
}

export function getProductiveTimecard(timecardDoc: IObjectTimecardDocument): ITimecard {
    const productiveTimecard = initTimecard(_.cloneDeep(timecardDoc.MasterTimecard), true)
    calculateTimecardTotals(productiveTimecard)
    return productiveTimecard
}

export function getNonProductiveTimecard(timecardDoc: IObjectTimecardDocument): ITimecard {
    const nonProductiveTimecard = initTimecard(_.cloneDeep(timecardDoc.MasterTimecard), false)
    calculateTimecardTotals(nonProductiveTimecard)
    return nonProductiveTimecard
}

function calculateTimecardTotals(timecard: ITimecard) {
    timecard.TimecardWeeks.forEach(week => {
        week.Days.forEach(day => calculateTimecardDayTotals(day))
        calculateTimecardWeekTotals(week)
    })

    var timecardWeeksExist = timecard.TimecardWeeks.length > 0
    timecard.TotalProductiveInMinutes = timecardWeeksExist ? timecard.TimecardWeeks[0].TotalProductiveInMinutes : 0
    timecard.TotalDurationInMinutes = timecardWeeksExist ? timecard.TimecardWeeks[0].TotalDurationInMinutes : 0
    timecard.TotalRegularPayDurationInMinutes = timecardWeeksExist ? timecard.TimecardWeeks[0].TotalRegularPayDurationInMinutes : 0
    timecard.TotalOvertimePayDurationInMinutes = timecardWeeksExist ? timecard.TimecardWeeks[0].TotalOvertimePayDurationInMinutes : 0
    timecard.TotalDoubleOvertimePayDurationInMinutes = timecardWeeksExist ? timecard.TimecardWeeks[0].TotalDoubleOvertimePayDurationInMinutes : 0
}

function calculateTimecardWeekTotals(timecardWeek: ITimecardWeek) {
    timecardWeek.TimeTotals = timeTotalsHelper.getTimeTotalsFromList(timecardWeek.Days.map(i => i.TimeTotals))

    timecardWeek.TotalProductiveInMinutes = timecardWeek.TimeTotals.TotalProductiveMinutes
    timecardWeek.TotalDurationInMinutes = timecardWeek.TimeTotals.TotalMinutes
    timecardWeek.TotalRegularPayDurationInMinutes = timecardWeek.TimeTotals.RegularMinutes
    timecardWeek.TotalOvertimePayDurationInMinutes = timecardWeek.TimeTotals.OvertimeMinutes
    timecardWeek.TotalDoubleOvertimePayDurationInMinutes = timecardWeek.TimeTotals.DoubleOvertimeMinutes

    timecardWeek.PayTotal = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "PayTotal"))
    timecardWeek.PayTotalRegularTime = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "PayTotalRegularTime"))
    timecardWeek.PayTotalOvertime = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "PayTotalOvertime"))
    timecardWeek.PayTotalDoubleOvertime = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "PayTotalDoubleOvertime"))

    timecardWeek.BillTotal = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "BillTotal"))
    timecardWeek.BillTotalRegularTime = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "BillTotalRegularTime"))
    timecardWeek.BillTotalOvertime = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "BillTotalOvertime"))
    timecardWeek.BillTotalDoubleOvertime = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "BillTotalDoubleOvertime"))

    timecardWeek.NetTotal = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "NetTotal"))
    timecardWeek.BurdenTotal = utils.roundToTwo(utils.sumOfNumberValues(timecardWeek.Days, "BurdenTotal"))

    timecardWeek.MarginTotal = timecardWeek.BillTotal == 0 || timecardWeek.NetTotal == 0 ? 0 : utils.roundToTwo((timecardWeek.NetTotal / timecardWeek.BillTotal) * 100)
}

function calculateTimecardDayTotals(timecardDay: ITimecardDay) {
    var allExpenses = []
    if (timecardDay.TimeExpenses.length > 0) {
        allExpenses = allExpenses.concat(timecardDay.TimeExpenses)
    }
    if (timecardDay.Expenses.length > 0) {
        allExpenses = allExpenses.concat(timecardDay.Expenses)
    }

    timecardDay.TimeTotals = timeTotalsHelper.getTimeTotals(timecardDay.PunchSets, allExpenses)

    timecardDay.TotalProductiveInMinutes = timecardDay.TimeTotals.TotalProductiveMinutes
    timecardDay.TotalDurationInMinutes = timecardDay.TimeTotals.TotalMinutes
    timecardDay.TotalRegularPayDurationInMinutes = timecardDay.TimeTotals.RegularMinutes
    timecardDay.TotalOvertimePayDurationInMinutes = timecardDay.TimeTotals.OvertimeMinutes
    timecardDay.TotalDoubleOvertimePayDurationInMinutes = timecardDay.TimeTotals.DoubleOvertimeMinutes
}

function initTimecard(timecard: ITimecard, filterProductive: boolean): ITimecard {
    if (timecard.ApproachingOvertime) {
        timecard.ApproachingOvertime.HoursToWeeklyOT = 0
        timecard.ApproachingOvertime.HoursToWeeklyOT2 = 0
    }
    if (timecard.TimecardWeeks) {
        timecard.TimecardWeeks.forEach(week => initTimecardWeek(week, filterProductive))
    }
    if (timecard.FullPunchSets) {
        timecard.FullPunchSets = timecard.FullPunchSets.filter(i => filterProductive ? isProductive(i.PayTypeId) : isNonProductive(i.PayTypeId))
    }
    if (timecard.MissingPunches) {
        timecard.MissingPunches = timecard.MissingPunches.filter(i => filterProductive ? isProductive(i.PayTypeId) : isNonProductive(i.PayTypeId))
    }
    timecard.TotalProductiveInMinutes = 0
    timecard.TotalDurationInMinutes = 0
    timecard.TotalRegularPayDurationInHours = 0
    timecard.TotalOvertimePayDurationInHours = 0
    timecard.TotalDoubleOvertimePayDurationInHours = 0

    return timecard
}

function initTimecardWeek(timecardWeek: ITimecardWeek, filterProductive: boolean) {
    timecardWeek.Days &&
        timecardWeek.Days.forEach(day => initTimecardDay(day, filterProductive))
    timecardWeek.TotalProductiveInMinutes = 0
    timecardWeek.TotalDurationInMinutes = 0
    timecardWeek.TotalRegularPayDurationInHours = 0
    timecardWeek.TotalOvertimePayDurationInHours = 0
    timecardWeek.TotalDoubleOvertimePayDurationInHours = 0
    timecardWeek.TimeTotals = timeTotalsHelper.getInitTimeTotals()
    timecardWeek.PayTotal = 0
    timecardWeek.PayTotalRegularTime = 0
    timecardWeek.PayTotalOvertime = 0
    timecardWeek.PayTotalDoubleOvertime = 0
    timecardWeek.BillTotal = 0
    timecardWeek.BillTotalRegularTime = 0
    timecardWeek.BillTotalOvertime = 0
    timecardWeek.BillTotalDoubleOvertime = 0
    timecardWeek.NetTotal = 0
    timecardWeek.BurdenTotal = 0
    timecardWeek.MarginTotal = 0
}

function initTimecardDay(timecardDay: ITimecardDay, filterProductive: boolean) {
    if (timecardDay.PunchSets && timecardDay.PunchSets.length > 0) {
        timecardDay.PunchSets = timecardDay.PunchSets.filter(i => filterProductive ? isProductive(i.PayTypeId) : isNonProductive(i.PayTypeId))
    }
    timecardDay.TimeTotals = timeTotalsHelper.getInitTimeTotals()
    timecardDay.TotalProductiveInMinutes = 0
    timecardDay.TotalDurationInMinutes = 0
    timecardDay.TotalRegularPayDurationInMinutes = 0
    timecardDay.TotalOvertimePayDurationInMinutes = 0
    timecardDay.TotalDoubleOvertimePayDurationInMinutes = 0
    if (timecardDay.TimeExpenses && timecardDay.TimeExpenses.length > 0) {
        timecardDay.TimeExpenses = timecardDay.TimeExpenses.filter(i => filterProductive ? isProductive(i.PayTypeId) : isNonProductive(i.PayTypeId))
    }
    if (timecardDay.Expenses && timecardDay.Expenses.length > 0) {
        timecardDay.Expenses = timecardDay.Expenses.filter(i => filterProductive ? isProductive(i.PayTypeId) : isNonProductive(i.PayTypeId))
    }
}

function isProductive(payTypeId: string): boolean {
    const payType = payGroupAndTypeHelper.getPayTypeById(payTypeId)
    return payType && !payType.NonProductiveTime
}

function isNonProductive(payTypeId: string): boolean {
    const payType = payGroupAndTypeHelper.getPayTypeById(payTypeId)
    return payType && payType.NonProductiveTime
}